import UndoIcon from '@mui/icons-material/Undo';
import { Column, Table } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Menu } from '@work4all/components';
import { SELECTION_COLUMN_ID } from '@work4all/components/lib/dataDisplay/basic-table/utils/makeRowsSelectable';

import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { OBJECT_LOCK_COLUMN_ID } from '../../../../../generic-column/GenericColumns';
import { columnsToMenuSectionsV8 } from '../../utils/columns-to-menu-sections-v8';
import { ColumnsMenuItem } from '../ColumnsMenuItem';

export interface IGroupingsProps {
  table: Table<unknown>;
  popoverState: ReturnType<typeof usePopoverState>;
  title?: string;
}

const GROUPING_COLUMNS_EXCEPTIONS = [
  SELECTION_COLUMN_ID,
  OBJECT_LOCK_COLUMN_ID,
];

function canGroupByColumn(column: Column<unknown, unknown>): boolean {
  return (
    column.getCanGroup() &&
    !column.getIsPinned() &&
    !GROUPING_COLUMNS_EXCEPTIONS.includes(column.id)
  );
}

export const GroupingsVisibiltyV8: React.FC<IGroupingsProps> = React.memo(
  function GroupingsVisibilty(props) {
    const { table, popoverState, title } = props;

    const columns = table.getAllFlatColumns();

    const canGroupColumns = useMemo(() => {
      return columns.filter(canGroupByColumn);
    }, [columns]);

    const { t } = useTranslation();
    const sections = columnsToMenuSectionsV8(canGroupColumns);

    const onResetColumns = () => {
      table.resetGrouping();
    };

    return (
      <Menu<Column<unknown>>
        title={title ?? t('INPUTS.GROUPING')}
        sections={sections}
        renderItem={(item) => {
          return (
            <ColumnsMenuItem
              label={item.label}
              isChecked={item.getIsGrouped?.() ?? false}
              hasSections={Boolean(item.sections)}
            />
          );
        }}
        open={popoverState.open}
        anchorEl={popoverState.anchorEl}
        onClose={popoverState.onClose}
        onItemClick={(col) => {
          col.toggleGrouping();
        }}
        actions={[
          {
            name: t('LISTS_PAGES.RESET_COLUMNS'),
            icon: <UndoIcon color="disabled" />,
            onClick: onResetColumns,
          },
        ]}
      />
    );
  }
);
